/* Patient Roster
   Route: /dashboard/triage now owns the triage-focused workflow;
   /dashboard/roster (this page) is the broader management view.

   Item 6.5 (Phase 5 security remediation, 24–25/08/2026): patient identity
   listing now comes from the governed backend route (GET /api/v1/users
   via lib/demographic.js's listPatients), which resolves platform_user
   -> patient_identity_link -> patient server-side and is tenant-scoped.
   This replaces a prior direct client-side Supabase query against the
   legacy `users` table.

   The devices query below is UNCHANGED and out of scope for this
   migration — it was not named in the Item 6 directive. It has no
   tenant_id filter; flagged separately for governance review, not
   fixed here.

   IDENTIFIER MAPPING — RESOLVED 25/08/2026: devices.patient_id is
   FK-constrained (fk_devices_patient_id) to canonical patient.id, not
   the legacy `users.id` and not platform_user.id. Confirmed via
   information_schema, not inferred from code. The device lookup below
   therefore keys off `patient.patientId` (the canonical patient_id
   returned by GET /api/v1/users), NOT `patient.id` (which stays
   platform_user_id — always present, used for React keys/selection,
   but not the correct join key for devices).

   Consequence: patients without a canonical patient_id yet (Stage 2 not
   run for them) will correctly show "No device" rather than a real
   device status, until that linkage exists. This is expected, not a bug.
*/
import React, { useState, useEffect, useCallback, useMemo } from "react";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../common/SafeIcon";
import supabase from "../supabase/supabase";
import { listPatients } from "../lib/demographic";

const {
  FiSearch,
  FiRefreshCw,
  FiWifi,
  FiWifiOff,
  FiClock,
  FiAlertCircle,
  FiEye,
  FiPhone,
  FiMessageSquare,
} = FiIcons;

const DEVICE_STATUS_STYLES = {
  active: "text-emerald-600 bg-emerald-50 border-emerald-200",
  inactive: "text-gray-500 bg-gray-50 border-gray-200",
  error: "text-rose-600 bg-rose-50 border-rose-200",
  none: "text-gray-400 bg-gray-50 border-gray-200",
};

function formatTimestamp(ts) {
  if (!ts) return "Never";
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const PatientRoster = () => {
  const [patients, setPatients] = useState([]);
  const [devicesByPatient, setDevicesByPatient] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [deviceFilter, setDeviceFilter] = useState("all");

  const fetchRoster = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const platformUsers = await listPatients({ limit: 500 });

      const { data: devices, error: devicesError } = await supabase
        .from("devices")
        .select("patient_id, status, last_sync_at, provider, device_name")
        .order("last_sync_at", { ascending: false });
      if (devicesError) throw devicesError;

      // Most-recently-synced device per patient, mirroring the same
      // pattern DeviceSourcePanel.jsx uses for a single patient.
      // Keyed on canonical patient.id, matching devices' FK.
      const latestByPatient = {};
      (devices || []).forEach((d) => {
        if (!latestByPatient[d.patient_id]) latestByPatient[d.patient_id] = d;
      });
      setDevicesByPatient(latestByPatient);

      setPatients(
        (platformUsers || []).map((u) => ({
          id: u.platform_user_id, // React key / selection identity — always present
          patientId: u.patient_id, // canonical link — null until Stage 2 exists for this user; USE THIS for devices lookup
          name: u.display_name || u.email || u.platform_user_id,
          condition: "",
        })),
      );
    } catch (err) {
      console.error(
        "[PatientRoster] fetch error:",
        err?.response?.data?.message || err?.message || err,
      );
      setError("Could not load the patient roster. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoster();
  }, [fetchRoster]);

  const filtered = useMemo(() => {
    return patients.filter((p) => {
      const matchesSearch = p.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const device = devicesByPatient[p.patientId];
      const status = device?.status ?? "none";
      const matchesDevice = deviceFilter === "all" || status === deviceFilter;
      return matchesSearch && matchesDevice;
    });
  }, [patients, devicesByPatient, searchTerm, deviceFilter]);

  return (
    <div className="min-h-screen text-slate-900 bg-[#FDFDFD] p-4 lg:p-8">
      <div className="max-w-6xl mx-auto">
        <header className="mb-6">
          <h1 className="text-2xl lg:text-3xl font-black tracking-tight text-gray-900">
            Patient Roster
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Every patient under your care, with device connectivity at a glance.
          </p>
        </header>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200 flex flex-wrap items-centergap-3 justify-between">
            <div className="relative flex-1 min-w-[200px]">
              <SafeIcon
                icon={FiSearch}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search patients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
              />
            </div>
            <select
              value={deviceFilter}
              onChange={(e) => setDeviceFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700"
            >
              <option value="all">All devices</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="error">Error</option>
              <option value="none">No device</option>
            </select>
            <button
              onClick={fetchRoster}
              disabled={isLoading}
              className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
            >
              <SafeIcon
                icon={FiRefreshCw}
                className={isLoading ? "animate-spin" : ""}
              />
              Refresh
            </button>
          </div>

          {error ? (
            <div className="p-6 flex items-start gap-2.5 text-rose-700 bg-rose-50 m-4 rounded-xl">
              <SafeIcon icon={FiAlertCircle} className="mt-0.5 flex-shrink-0" />
              <p className="text-sm font-medium">{error}</p>
            </div>
          ) : isLoading ? (
            <div className="p-10 text-center text-sm text-gray-500">
              Loading roster...
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-gray-400">
              No patients match this search.
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs font-bold uppercase tracking-widest text-gray-400 border-b border-gray-100">
                  <th className="px-4 py-3">Patient</th>
                  <th className="px-4 py-3">Device Status</th>
                  <th className="px-4 py-3">Last Sync</th>
                  <th className="px-4 py-3">Assigned Clinician</th>
                  <th className="px-4 py-3">Last Review</th>
                  <th className="px-4 py-3">Quick Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((patient) => {
                  const device = devicesByPatient[patient.patientId];
                  const status = device?.status ?? "none";
                  return (
                    <tr
                      key={patient.id}
                      className="border-b border-gray-50 last:border-0 text-sm"
                    >
                      <td className="px-4 py-3">
                        <p className="font-semibold text-gray-900">
                          {patient.name}
                        </p>
                        {patient.condition && (
                          <p className="text-xs text-gray-400">
                            {patient.condition}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-lg border capitalize ${DEVICE_STATUS_STYLES[status]}`}
                        >
                          <SafeIcon
                            icon={status === "active" ? FiWifi : FiWifiOff}
                            className="text-xs"
                          />
                          {status === "none" ? "No device" : status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500">
                        <span className="inline-flex items-center gap-1.5">
                          <SafeIcon icon={FiClock} className="text-gray-400" />
                          {formatTimestamp(device?.last_sync_at)}
                        </span>
                      </td>
                      {/* Migration/integration-phase placeholder — see file
                          header. Replace with real clinician name or
                          "Unassigned" once DB Schema V2's assignment
                          structure is operational. */}
                      <td className="px-4 py-3 text-gray-400 italic">
                        Not yet tracked
                      </td>
                      {/* Same for the HITL last-review timestamp — replace
                          with the real date or "No review recorded". */}
                      <td className="px-4 py-3 text-gray-400 italic">
                        Not yet tracked
                      </td>
                      {/* Quick Actions — intentionally disabled. No patient-detail route,
    Call, or Message endpoint exists yet in the backend. Rendering
    as visibly disabled controls rather than wiring to nonexistent
    routes, consistent with how HandshakeService.js is treated
    elsewhere. Re-enable each button individually as its backend
    dependency lands — do not enable all three at once by default. */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            disabled
                            title="Patient detail view — coming soon"
                            className="p-1.5 rounded-lg border border-gray-200 text-gray-300 cursor-not-allowed"
                          >
                            <SafeIcon icon={FiEye} className="text-sm" />
                          </button>
                          <button
                            type="button"
                            disabled
                            title="Call — not yet available"
                            className="p-1.5 rounded-lg border border-gray-200 text-gray-300 cursor-not-allowed"
                          >
                            <SafeIcon icon={FiPhone} className="text-sm" />
                          </button>
                          <button
                            type="button"
                            disabled
                            title="Message — not yet available"
                            className="p-1.5 rounded-lg border border-gray-200 text-gray-300 cursor-not-allowed"
                          >
                            <SafeIcon
                              icon={FiMessageSquare}
                              className="text-sm"
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientRoster;